/**
 * CSP-2788 (T14, T16) - Esito di una chiamata al BFF con lo status HTTP, che l'interceptor di
 * axiosInstance perde: sugli errori rigetta col solo body del BE (o col testo fisso
 * 'Something went wrong' se il body manca), quindi un 403 senza body e un 500 sono indistinguibili.
 *
 * Le chiamate che devono distinguere 403/404/409/422 passano `VALIDA_FINO_A_4XX` come
 * `validateStatus`: i 4xx tornano come risposta normale, con status e body, e l'interceptor non li
 * vede; il 401 resta un errore, cosi' il refresh del token dell'interceptor continua a funzionare.
 */

/** validateStatus per axios: 2xx-4xx sono risposte, tranne il 401 (refresh del token). */
export const VALIDA_FINO_A_4XX = (status) => status >= 200 && status < 500 && status !== 401;

/** ExceptionObject di os-sinistri: status = dominio * 1000 + status HTTP (es. 9422). */
const DOMINIO_ERRORI = 1000;

const MAX_ANNIDAMENTO = 3;

/**
 * Il messaggio del BE da un body di errore: il BFF inoltra il body di os-sinistri a volte come oggetto,
 * a volte come stringa JSON, e il `message` puo' contenere a sua volta l'envelope JSON di un altro
 * servizio. Si scende fino al testo, al massimo `MAX_ANNIDAMENTO` livelli.
 * @param {*} data body della risposta
 * @returns {string|null} il messaggio, null se non c'e'
 */
export const messaggioDelBe = (data, livello = 0) => {
  if (data === null || data === undefined || livello > MAX_ANNIDAMENTO) return null;
  if (typeof data === 'string') {
    const testo = data.trim();
    if (!testo) return null;
    try {
      const interno = JSON.parse(testo);
      if (interno && typeof interno === 'object') return messaggioDelBe(interno, livello + 1) || testo;
      return testo;
    } catch {
      return testo;
    }
  }
  if (typeof data === 'object' && data.message !== undefined) {
    return messaggioDelBe(data.message, livello + 1);
  }
  return null;
};

/** Status HTTP di un body ExceptionObject (9422 -> 422), null se il body non ne porta uno. */
const statusDalBody = (data) => {
  const body = typeof data === 'string' ? (() => {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  })() : data;
  const status = Number(body?.status);
  if (!Number.isInteger(status) || status <= 0) return null;
  return status > DOMINIO_ERRORI ? status % DOMINIO_ERRORI : status;
};

/**
 * Status HTTP da un errore: errore axios (`response.status`) oppure body rigettato dall'interceptor.
 * @returns {number|null} null se non e' ricavabile (rete giu', errore JavaScript)
 */
export const statoHttpDaErrore = (errore) => {
  if (errore?.response?.status) return errore.response.status;
  if (errore?.response) return statusDalBody(errore.response.data);
  if (errore instanceof Error) return null;
  return statusDalBody(errore);
};

/** Body di un errore: quello della risposta axios, oppure quello rigettato dall'interceptor. */
export const corpoDaErrore = (errore) => {
  if (errore?.response) return errore.response.data;
  if (errore instanceof Error) return null;
  return errore;
};

/**
 * Esegue la chiamata e restituisce sempre `{ status, data }`: status null quando non e' ricavabile
 * (rete, 5xx senza body, errore JavaScript). Non rigetta mai.
 * @param {function} chiamata funzione che restituisce la promise axios
 * @returns {Promise<{status: number|null, data: *}>}
 */
export const esitoHttp = async (chiamata) => {
  try {
    const risposta = await chiamata();
    return { status: risposta?.status ?? 200, data: risposta?.data };
  } catch (errore) {
    return { status: statoHttpDaErrore(errore), data: corpoDaErrore(errore) };
  }
};

export const isEsitoOk = (status) => status >= 200 && status < 300;
